/**
 * /el/guides/plano-trapezion-i-pinakas-theseon
 *
 * The Greek counterpart of `wedding-seating-chart-vs-seating-plan.en.js`, and
 * the guide that changes most in translation — deliberately, because its
 * English subject does not exist in Greek.
 *
 * ── Why this is not "seating chart vs seating plan" in Greek ─────────────
 * The English article resolves an ambiguity between two ENGLISH phrases that
 * mean the same thing in different markets. Greek has no such pair: couples,
 * κτήματα and διοργανωτές all say «πλάνο τραπεζιών» (with «διάταξη τραπεζιών»
 * as a vendor variant), and inventing a Greek counterpart of the distinction
 * would be manufacturing a problem in order to answer it.
 *
 * What Greek couples DO confuse is a different pair, and it is a real one: the
 * working assignment they build, and the board their guests read at the
 * entrance. Those genuinely are two different objects with two different
 * audiences, and the second is produced from the first. So the article keeps
 * the English guide's actual substance — assignment vs display, table-level vs
 * seat-level, room layout as a separate layer, the order the work happens in —
 * and drops only the part that was about English vocabulary.
 *
 * The regional-usage section is likewise gone. "Particularly common in the
 * United States" is useful to a reader deciding which of two words to type;
 * it is trivia to a reader who only ever had one.
 *
 * ── Terminology, and how far it is asserted ──────────────────────────────
 * Greek usage is not uniform either, so every term is introduced as one the
 * reader will HEAR («θα το ακούσετε και ως…»), never as the correct name for
 * a thing. The reader's own venue is the authority, which is the English
 * article's rule kept intact.
 *
 * «Κάτοψη» is deliberately not used anywhere, even though it is the ordinary
 * Greek word for a floor plan. It is on the banned list for the whole Greek
 * seating cluster — Agapaé has no floor-plan designer, and the word appearing
 * in a Greek seating article is exactly how a reader would conclude otherwise.
 * The room layout is called «η διάταξη της αίθουσας», which is what a κτήμα
 * says on the phone.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * Four, all in the closing section:
 *
 *   - «Τα εργαλεία για τα τραπέζια είναι διαθέσιμα στο πακέτο Heirloom.»
 *   - seating works from the guests already in the wedding
 *   - confirmed reception attendees can be moved into table assignments
 *   - the resulting plan can be reviewed
 *
 * And a fifth prohibition on top of the cluster's four, inherited from the
 * English module because this guide's subject invites the mistake: it
 * discusses guest-facing boards, printed displays and cards at length, and
 * Agapaé produces none of them. Nothing here may suggest it does.
 *
 * No copy in this file contains "&", "<" or ">" — the seam test compares
 * against tag-stripped HTML without decoding entities.
 */

const weddingSeatingChartVsSeatingPlanEl = {
  slug: "plano-trapezion-i-pinakas-theseon",

  title: "Πλάνο τραπεζιών ή πίνακας θέσεων; Τι διαφέρει στην πράξη",
  metaTitle: "Πλάνο τραπεζιών ή πίνακας θέσεων; Τι διαφέρει στην πράξη | Agapaé",
  metaDescription:
    "Πλάνο τραπεζιών, πίνακας στην είσοδο, καρτελάκια τραπεζιού και θέσης, διάταξη αίθουσας: τι είναι το καθένα και τι χρειάζεστε πραγματικά για τη δεξίωσή σας.",

  eyebrow: "Τραπέζια γάμου",

  hero: {
    src: "/assets/images/moments/moment-1.jpg",
    // The photograph, described as it is — the same image the English module
    // carries, and nothing in it is about seating.
    alt: "Ασπρόμαυρη κοντινή λήψη των χεριών της νύφης και του γαμπρού πιασμένων χαλαρά, με τη δαντέλα του νυφικού στη μια άκρη του κάδρου και ήρεμο νερό θολό στο βάθος",
    width: 6983,
    height: 3436,
  },

  intro: [
    `Όσο ετοιμάζετε τη δεξίωση, θα ακούσετε τα ίδια πράγματα με πολλά ονόματα: πλάνο τραπεζιών, διάταξη τραπεζιών, πίνακας, ταμπλό, καρτελάκια, placement.`,
    `Τα περισσότερα από αυτά δεν είναι διαφορετικά έγγραφα.`,
    `Υπάρχει όμως μια διάκριση που αξίζει πραγματικά να ξεκαθαρίσετε, γιατί μπερδεύει σχεδόν όλους: άλλο το πλάνο που φτιάχνετε εσείς και άλλο αυτό που βλέπουν οι καλεσμένοι μπαίνοντας.`,
    `Το πρώτο είναι η δουλειά. Το δεύτερο είναι το αποτέλεσμά της, τυπωμένο.`,
    `Μόλις το ξεχωρίσετε, όλα τα υπόλοιπα μπαίνουν στη θέση τους.`,
  ],

  sections: [
    {
      heading: "Το πλάνο και ο πίνακας δεν είναι το ίδιο πράγμα",
      content: [
        `Το πλάνο τραπεζιών είναι η κατανομή: ποιος καλεσμένος κάθεται σε ποιο τραπέζι.`,
        `Για παράδειγμα:`,
        {
          type: "list",
          items: [
            `Άννα και Μάρκος → Τραπέζι 3`,
            `Σοφία → Τραπέζι 3`,
            `Δημήτρης και Ελένη → Τραπέζι 7`,
          ],
        },
        `Αυτή η πληροφορία είναι η βάση για όλα τα υπόλοιπα.`,
        `Ο πίνακας που στήνεται στην είσοδο της δεξίωσης — θα τον ακούσετε και ως ταμπλό ή ως placement — είναι μια παρουσίαση αυτής της ίδιας πληροφορίας, φτιαγμένη για να τη διαβάσει κάποιος όρθιος, με ένα ποτήρι στο χέρι, σε τριάντα δευτερόλεπτα.`,
        `Και τα δύο περιγράφουν τα ίδια τραπέζια. Απλώς απευθύνονται σε διαφορετικούς ανθρώπους.`,
      ],
    },
    {
      heading: "Γιατί ακούτε τόσα ονόματα για το ίδιο πράγμα",
      content: [
        `Ο κάθε συνεργάτης το λέει όπως το έμαθε.`,
        `Ο χώρος μπορεί να σας ζητήσει «τη διάταξη των τραπεζιών», ο τυπογράφος «το πλάνο», ο wedding planner «το placement», και όλοι να εννοούν την ίδια λίστα με ονόματα και αριθμούς.`,
        `Δεν χρειάζεται να τα δείτε ως ξεχωριστά παραδοτέα.`,
        `Το πρακτικό είναι να ρωτάτε τι ακριβώς περιμένει ο καθένας να λάβει και σε ποια μορφή.`,
        `Και όταν έχετε αμφιβολία, χρησιμοποιήστε τον όρο που χρησιμοποιεί ο χώρος σας. Είναι ο πιο εύκολος τρόπος να μιλάτε όλοι για το ίδιο πράγμα.`,
      ],
    },
    {
      heading: "Τι δείχνει στην πράξη ένα πλάνο τραπεζιών",
      content: [
        `Στην απλούστερη μορφή του, το πλάνο απαντά σε μία ερώτηση:`,
        `Σε ποιο τραπέζι κάθεται αυτός ο καλεσμένος;`,
        `Συνήθως περιέχει:`,
        {
          type: "list",
          items: [
            `τα ονόματα των καλεσμένων`,
            `τον αριθμό ή το όνομα του κάθε τραπεζιού`,
            `τη σχέση του καθενός με το τραπέζι στο οποίο μπήκε`,
          ],
        },
        `Μπορεί να είναι ταξινομημένο αλφαβητικά ανά όνομα ή ομαδοποιημένο ανά τραπέζι.`,
        `Αλφαβητικά μοιάζει κάπως έτσι:`,
        {
          type: "list",
          items: [`Μαρία → Τραπέζι 2`, `Νίκος → Τραπέζι 5`, `Σοφία → Τραπέζι 2`],
        },
        `Ανά τραπέζι, το ίδιο πράγμα δείχνει έτσι:`,
        { type: "subheading", text: "Τραπέζι 2" },
        {
          type: "list",
          items: [`Μαρία`, `Σοφία`, `Ανδρέας`, `Ελένη`],
        },
        { type: "subheading", text: "Τραπέζι 5" },
        {
          type: "list",
          items: [`Νίκος`, `Χριστίνα`, `Γιώργος`, `Άννα`],
        },
        `Η πληροφορία είναι ακριβώς η ίδια.`,
        `Ποια μορφή σας βολεύει εξαρτάται από το πώς θέλετε να βρίσκει ο καλεσμένος το τραπέζι του και από το ποιος άλλος θα δουλέψει με το ίδιο χαρτί.`,
      ],
    },
    {
      heading: "Ο πίνακας στην είσοδο",
      content: [
        `Ο πίνακας είναι η πιο συνηθισμένη λύση στις ελληνικές δεξιώσεις: ένα μεγάλο κομμάτι που το διαβάζουν όλοι μπαίνοντας.`,
        `Δουλεύει καλά όταν:`,
        {
          type: "list",
          items: [
            `μπαίνει σε σημείο που περνούν υποχρεωτικά όλοι`,
            `φωτίζεται αρκετά ώστε να διαβάζεται και το βράδυ`,
            `τα γράμματα είναι μεγάλα και όχι διακοσμητικά`,
            `η σειρά είναι η ίδια για όλους — ή αλφαβητικά ή ανά τραπέζι, όχι μισό μισό`,
          ],
        },
        `Το πιο συχνό λάθος δεν είναι το περιεχόμενο· είναι το μέγεθος. Ένας πίνακας που πρέπει να τον πλησιάσει κανείς για να τον διαβάσει δημιουργεί ουρά ακριβώς τη στιγμή που φτάνουν όλοι μαζί.`,
        `Αν οι καλεσμένοι είναι πολλοί, δύο ίδιοι πίνακες σε δύο σημεία λύνουν το πρόβλημα καλύτερα από έναν μεγαλύτερο.`,
      ],
    },
    {
      heading: "Τα καρτελάκια τραπεζιού",
      content: [
        `Αντί για πίνακα, κάποια ζευγάρια βάζουν μικρές κάρτες στην είσοδο, μία ανά καλεσμένο ή ανά ζευγάρι.`,
        `Για παράδειγμα:`,
        `Μαρία και Νίκος`,
        `Τραπέζι 5`,
        `Λύνουν το ίδιο πρόβλημα με τον πίνακα: να βρει ο καλεσμένος πού κάθεται.`,
        `Αυτό που αλλάζει είναι η μορφή.`,
        `Ο πίνακας είναι ένα σημείο που το διαβάζουν όλοι.`,
        `Τα καρτελάκια είναι πολλά μικρά κομμάτια που τα παίρνει ο καθένας.`,
        `Δεν χρειάζεστε και τα δύο μόνο και μόνο για να μάθει ο κόσμος το τραπέζι του. Η επιλογή έχει να κάνει με τον χώρο της εισόδου, με το πόσο μαζεμένοι φτάνουν οι καλεσμένοι και με το ύφος της δεξίωσης.`,
      ],
    },
    {
      heading: "Τα καρτελάκια θέσης",
      content: [
        `Το καρτελάκι θέσης πάει ένα βήμα παραπέρα.`,
        `Δεν λέει σε ποιο τραπέζι πηγαίνει κάποιος, αλλά σε ποια θέση κάθεται.`,
        `Για παράδειγμα:`,
        `Μαρία`,
        `Τραπέζι 5 — Θέση 3`,
        `Άρα υπάρχουν δύο επίπεδα κατανομής:`,
        {
          type: "list",
          items: [
            `Κατανομή σε τραπέζι: η Μαρία κάθεται στο Τραπέζι 5.`,
            `Κατανομή σε θέση: η Μαρία κάθεται σε συγκεκριμένο σημείο του Τραπεζιού 5.`,
          ],
        },
        `Στους περισσότερους γάμους οι καλεσμένοι μαθαίνουν το τραπέζι τους και διαλέγουν μόνοι τους καρέκλα.`,
        `Οι συγκεκριμένες θέσεις έχουν νόημα σε πιο επίσημες δεξιώσεις, όπου το κάθε σερβίτσιο είναι ήδη αποφασισμένο.`,
        `Το σημαντικό είναι ότι το καρτελάκι θέσης δεν αντικαθιστά το πλάνο. Είναι ένα πιο αναλυτικό στρώμα πάνω σε αυτό.`,
      ],
    },
    {
      heading: "Η διάταξη της αίθουσας είναι άλλο πράγμα",
      content: [
        `Το πλάνο λέει ποιος κάθεται πού.`,
        `Η διάταξη της αίθουσας λέει πού βρίσκονται τα τραπέζια μέσα στον χώρο.`,
        `Το σχεδιάγραμμα που κρατά ο χώρος σας μπορεί να δείχνει:`,
        {
          type: "list",
          items: [
            `το τραπέζι 1 δίπλα στην πίστα`,
            `τα τραπέζια 2 και 3 κοντά στο μπαρ`,
            `το νυφικό τραπέζι μπροστά`,
            `τη θέση της μουσικής`,
            `τις εισόδους και τις εξόδους`,
          ],
        },
        `Το πλάνο συνδέει τους ανθρώπους με αυτά τα τραπέζια.`,
        `Δείτε τα ως δύο στρώματα:`,
        `Διάταξη αίθουσας → πού είναι τα τραπέζια`,
        `Πλάνο τραπεζιών → ποιοι κάθονται στο καθένα`,
        `Δουλεύουν μαζί, αλλά δεν είναι το ίδιο χαρτί — και τα ετοιμάζουν διαφορετικοί άνθρωποι.`,
      ],
    },
    {
      heading: "Τραπέζι ή θέση; Πόση λεπτομέρεια χρειάζεστε",
      content: [
        `Στους περισσότερους γάμους αρκεί η κατανομή σε τραπέζι.`,
        `Αποφασίζετε δηλαδή:`,
        `«Η οικογένεια Παπαδοπούλου κάθεται στο Τραπέζι 4.»`,
        `Και οι ίδιοι διαλέγουν καρέκλα όταν φτάσουν.`,
        `Εναλλακτικά μπορείτε να ορίσετε συγκεκριμένες θέσεις:`,
        `«Η Μαρία κάθεται στο Τραπέζι 4, θέση 2.»`,
        `Το αν χρειάζεστε αυτό το επίπεδο εξαρτάται από το ύφος της δεξίωσης, από το πόσο επίσημη είναι και από το πόσο θέλετε να ελέγχετε την τελική εικόνα.`,
        `Μη θεωρήσετε ότι «πλάνο τραπεζιών» σημαίνει αυτόματα ότι κάθε καρέκλα έχει όνομα.`,
        `Η κατανομή σε τραπέζι είναι από μόνη της ένα ολοκληρωμένο πλάνο.`,
      ],
    },
    {
      heading: "Τι χρειάζεστε τελικά",
      content: [
        `Για τα περισσότερα ζευγάρια η αλυσίδα είναι απλή:`,
        {
          type: "steps",
          items: [
            `Λίστα καλεσμένων — να ξέρετε ποιοι έρχονται πραγματικά.`,
            `Κατανομή σε τραπέζια — ποιοι ανήκουν σε ποιο τραπέζι.`,
            `Πλάνο τραπεζιών — η κατανομή κρατημένη σε ένα σημείο.`,
            `Ό,τι βλέπουν οι καλεσμένοι — πίνακας στην είσοδο ή καρτελάκια.`,
            `Καρτελάκια θέσης, αν χρειάζονται — μόνο αν η δεξίωσή σας τα θέλει.`,
          ],
        },
        `Δεν χρειάζεται ξεχωριστό έγγραφο για κάθε βήμα.`,
        `Χρειάζεται ένα αξιόπιστο σύνολο κατανομών πριν φτιαχτεί οτιδήποτε θα δουν οι καλεσμένοι ή οι συνεργάτες σας.`,
      ],
    },
    {
      heading: "Ένα απλό παράδειγμα",
      content: [
        `Έστω ότι έχετε 80 επιβεβαιωμένους καλεσμένους και οκτώ τραπέζια.`,
        `Το πλάνο εργασίας σας μοιάζει κάπως έτσι:`,
        { type: "subheading", text: "Τραπέζι 1" },
        {
          type: "list",
          items: [
            `Ζευγάρι Α`,
            `Ζευγάρι Β`,
            `Καλεσμένος Γ`,
            `Καλεσμένη Δ`,
            `Καλεσμένος Ε`,
            `Καλεσμένη Ζ`,
            `Καλεσμένος Η`,
            `Καλεσμένη Θ`,
          ],
        },
        { type: "subheading", text: "Τραπέζι 2" },
        {
          type: "list",
          items: [
            `Ζευγάρι Ι`,
            `Ζευγάρι Κ`,
            `Καλεσμένος Λ`,
            `Καλεσμένη Μ`,
            `Καλεσμένος Ν`,
            `Καλεσμένη Ξ`,
            `Καλεσμένος Ο`,
            `Καλεσμένη Π`,
          ],
        },
        `Και ούτω καθεξής.`,
        `Από την ίδια αυτή κατανομή βγάζετε τον πίνακα που θα δουν οι καλεσμένοι.`,
        `Ή, αν προτιμάτε, τα καρτελάκια.`,
        `Κι αν θέλετε ονόματα και σε θέσεις, αυτό προστίθεται χωριστά.`,
        `Το ζητούμενο είναι ότι η κατανομή είναι η πηγή. Η μορφή που θα πάρει είναι ξεχωριστή απόφαση.`,
      ],
    },
    {
      heading: "Πότε φτιάχνεται το πλάνο",
      content: [
        `Μπορείτε να το σκέφτεστε από νωρίς, αλλά γίνεται αξιόπιστο μόνο όταν ξέρετε ποιοι έρχονται.`,
        `Μια πρακτική σειρά είναι:`,
        {
          type: "steps",
          items: [
            `Φτιάξτε και συντηρήστε τη λίστα καλεσμένων.`,
            `Συγκεντρώστε και ελέγξτε τις απαντήσεις.`,
            `Δουλέψτε το πλάνο πάνω στους επιβεβαιωμένους.`,
            `Δείτε το μαζί, και όπου χρειάζεται με τον χώρο ή τον wedding planner σας.`,
            `Ετοιμάστε τον πίνακα ή τα καρτελάκια.`,
          ],
        },
        `Έχει σημασία, γιατί μία αλλαγή αγγίζει συνήθως περισσότερα από ένα τραπέζια. Μια ακύρωση, ένας καλεσμένος που προστίθεται ή μια παρέα που μεγαλώνει σας γυρίζουν πίσω στο πλάνο.`,
        `Δείτε το λοιπόν ως κάτι που εξελίσσεται μέχρι να κλειδώσουν οι τελευταίες λεπτομέρειες — και τυπώστε τελευταία.`,
      ],
    },
    {
      heading: "Από τη λίστα καλεσμένων στο πλάνο",
      content: [
        `Το πλάνο δεν στέκει μόνο του.`,
        `Ξεκινά από τους ανθρώπους που έχετε ήδη καταγράψει.`,
        `Η διαδρομή είναι:`,
        `λίστα καλεσμένων → απαντήσεις → επιβεβαιωμένοι → τραπέζια → κατανομή → ό,τι βλέπουν οι καλεσμένοι`,
        {
          type: "p",
          text: [
            `Γι' αυτό βοηθάει να μένει η κατανομή δεμένη με τη λίστα, αντί να ξαναγράφονται ονόματα σε τρία διαφορετικά αρχεία. Αν δεν έχετε ακόμη μαζέψει τις απαντήσεις, ξεκινήστε από το `,
            { text: "RSVP γάμου", href: "/wedding-rsvp" },
            `.`,
          ],
        },
        `Όταν κάποιος αλλάξει απάντηση ή χρειαστεί να μετακινηθεί σε άλλο τραπέζι, θέλετε να το βλέπετε αμέσως και σε ένα μέρος.`,
      ],
    },
    {
      heading: "Από τους όρους στο πραγματικό πλάνο",
      content: [
        `Μόλις κλειδώσει η κατανομή, το πλάνο γίνεται χρήσιμο και σε άλλους εκτός από τους καλεσμένους.`,
        `Ανάλογα με τη δεξίωση, βοηθά εσάς και τους συνεργάτες σας σε:`,
        {
          type: "list",
          items: [
            `το στήσιμο των τραπεζιών`,
            `το πού κατευθύνονται οι καλεσμένοι`,
            `το σερβίρισμα`,
            `τις ειδικές περιπτώσεις που θέλουν προσοχή`,
            `τις αλλαγές της τελευταίας στιγμής`,
          ],
        },
        `Αυτό που βλέπουν οι καλεσμένοι μπορεί να είναι ένας απλός τυπωμένος πίνακας. Αυτό που κρατάτε εσείς έχει περισσότερη λεπτομέρεια.`,
        `Το σημαντικό είναι να δουλεύουν όλοι πάνω στην ίδια, τελική κατανομή.`,
        {
          type: "note",
          text: `Τα εργαλεία για τα τραπέζια στο Agapaé είναι διαθέσιμα στο πακέτο Heirloom.`,
        },
        `Τα τραπέζια στο Agapaé ξεκινούν από τους καλεσμένους που ήδη έχετε στον γάμο σας: από τους επιβεβαιωμένους της δεξίωσης περνάτε στην κατανομή σε τραπέζια και ελέγχετε το πλάνο που προκύπτει.`,
        {
          type: "p",
          text: [
            `Αν ψάχνετε τη διαδικασία βήμα βήμα, δείτε το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            `. Για το ίδιο το προϊόν, δείτε τα `,
            { text: "Τραπέζια γάμου", href: "/wedding-seating" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Το πλάνο τραπεζιών και η διάταξη τραπεζιών είναι το ίδιο;`,
      a: `Στην πράξη ναι. Και οι δύο όροι περιγράφουν την κατανομή των καλεσμένων στα τραπέζια της δεξίωσης. Ποιον θα ακούσετε εξαρτάται από τον χώρο, τον wedding planner ή τον συνεργάτη με τον οποίο μιλάτε.`,
    },
    {
      q: `Τι διαφέρει το πλάνο από τον πίνακα στην είσοδο;`,
      a: `Το πλάνο είναι η κατανομή που φτιάχνετε και αλλάζετε εσείς. Ο πίνακας είναι η τυπωμένη μορφή της, φτιαγμένη για να τη διαβάσουν οι καλεσμένοι μέσα σε λίγα δευτερόλεπτα. Ο δεύτερος βγαίνει από το πρώτο.`,
    },
    {
      q: `Χρειάζομαι και πίνακα και καρτελάκια;`,
      a: `Όχι. Λύνουν το ίδιο πρόβλημα με διαφορετικό τρόπο — να βρει ο καλεσμένος το τραπέζι του. Διαλέξτε ανάλογα με τον χώρο της εισόδου, τον αριθμό των καλεσμένων και το ύφος της δεξίωσης.`,
    },
    {
      q: `Τι διαφέρει το καρτελάκι τραπεζιού από το καρτελάκι θέσης;`,
      a: `Το πρώτο λέει σε ποιο τραπέζι πηγαίνει ο καλεσμένος. Το δεύτερο λέει σε ποια θέση κάθεται πάνω σε αυτό. Το καρτελάκι θέσης είναι ένα πιο αναλυτικό στρώμα, όχι υποκατάστατο του πλάνου.`,
    },
    {
      q: `Πρέπει να ορίσω συγκεκριμένες θέσεις;`,
      a: `Όχι απαραίτητα. Στους περισσότερους ελληνικούς γάμους οι καλεσμένοι μαθαίνουν το τραπέζι τους και κάθονται όπου θέλουν σε αυτό. Οι συγκεκριμένες θέσεις είναι επιλογή και ταιριάζουν σε πιο επίσημες δεξιώσεις.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["wedding-seating-plan", "how-many-people-wedding-table"],

    product: [
      {
        href: "/wedding-seating",
        label: "Τραπέζια γάμου",
        blurb:
          "Δείτε πώς δουλεύουν τα τραπέζια στο Agapaé, από τους επιβεβαιωμένους καλεσμένους της δεξίωσης μέχρι την κατανομή τους στα τραπέζια. Διαθέσιμα στο πακέτο Heirloom.",
      },
    ],
  },
};

export default weddingSeatingChartVsSeatingPlanEl;
